import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Network,
  Search,
  Sparkles,
  TreePine,
  X,
} from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import { getFlowCommission } from "../../../../Redux/referral/slice";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import ReferralCommTable from "./ReferralCommTable";
import {
  PRODUCT_IMAGE_FALLBACK,
  earningsStatusClass,
  formatEarningsCurrency,
  formatEarningsLongDate,
  mapFlowItemToRecord,
  statusLabel,
} from "../earningsAdapters";
import "./SalarFlowPage.css";

const ReferralCommission = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { flowCommission } = useSelector(
    (state: RootState) => state.referral,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    // If startDate is set, wait until endDate is also filled before calling API
    if (dateRange.from && !dateRange.to) return;

    dispatch(
      getFlowCommission({
        page,
        limit,
        status: "ALL",
        sortOrder: "DESC",
        search: debouncedSearch || undefined,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [dispatch, page, limit, debouncedSearch, dateRange.from, dateRange.to]);

  const records = (flowCommission.data || []).map(mapFlowItemToRecord);
  const totalPages = flowCommission.pagination?.totalPages || 0;
  const totalRecords =
    flowCommission.pagination?.total ?? flowCommission.data?.length ?? 0;

  const go = (path: string, event?: React.MouseEvent) => {
    event?.stopPropagation();
    navigate(path);
  };

  return (
    <section className="ri-page">
      <div className="ri-shell">
        <header className="ri-page-header">
          <div className="ri-page-title">
            <span className="ri-title-icon">
              <Sparkles size={25} />
            </span>
            <div>
              <span className="ri-eyebrow">REFERRAL INCENTIVES</span>
              <h1>Salar Flow Incentives</h1>
              <p>
                View referral-network progress, received incentives, Nova
                network performance and earned badges.
              </p>
            </div>
          </div>
          <span className="ri-count-pill">{totalRecords} records</span>
        </header>

        <div className="ri-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>

          <div className="ri-date-picker">
            <DateRangePicker
              value={dateRange}
              onChange={(v) => {
                setDateRange(v);
                setPage(1);
              }}
            />
          </div>

          <div className="ri-search">
            <Search size={19} />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search incentives..."
              aria-label="Search incentives"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        <ReferralCommTable data={flowCommission.data || []} />

        <div className="ri-mobile-list">
          {records.map((record) => (
            <article
              key={record.id}
              className="ri-mobile-main-card"
              onClick={() => go(`/ReferralCommDetails?orderNo=${record.orderNo}`)}
            >
              <div className="ri-mobile-card-topline">
                <span className="ri-mobile-index">{record.serialNo}</span>
                <span className="ri-mobile-card-date">
                  <CalendarDays size={16} />
                  {formatEarningsLongDate(record.orderDate)}
                </span>
                <span
                  className={`ri-main-badge ri-main-badge-${
                    record.achieved ? "combination" : "regular"
                  }`}
                >
                  {record.achieved ? "Achieved" : "Not Achieved"}
                </span>
              </div>

              <div className="ri-mobile-product-row">
                <img src={PRODUCT_IMAGE_FALLBACK} alt="" />
                <div>
                  <span className="ri-mobile-label">PRODUCT</span>
                  <h2>{record.productName}</h2>
                  <p>{record.orderNo}</p>
                  <strong>
                    {formatEarningsCurrency(record.netAmount ?? record.referralIncentives)}
                  </strong>
                  {record.tdsAmount != null && (
                    <p>
                      TDS {formatEarningsCurrency(record.tdsAmount)}
                      {record.tdsIsEstimated ? " (est)" : ""} · Gross{" "}
                      {formatEarningsCurrency(record.grossAmount)}
                    </p>
                  )}
                </div>
              </div>

              <div className="ri-mobile-network-summary">
                <div>
                  <span>Flow Active</span>
                  <strong>
                    {record.activeContributors}/{record.contributionRequirement}
                  </strong>
                </div>
                <div>
                  <span>Nova Active</span>
                  <strong>
                    {record.novaActiveContributors}/
                    {record.novaContributionRequirement}
                  </strong>
                </div>
                <div>
                  <span>Status</span>
                  <strong>
                    <span className={earningsStatusClass(record.flowStatus)}>
                      {statusLabel(record.flowStatus)}
                    </span>
                  </strong>
                </div>
              </div>

              <div className="ri-mobile-quick-actions">
                <button
                  type="button"
                  onClick={(e) =>
                    go(`/ReferralCommissoinTeam?orderNo=${record.orderNo}`, e)
                  }
                >
                  <Network size={17} />
                  Flow Network
                </button>
                <button
                  type="button"
                  onClick={(e) =>
                    go(`/Tree?orderNo=${record.orderNo}&type=referral`, e)
                  }
                >
                  <TreePine size={17} />
                  Flow Tree
                </button>
                <button
                  type="button"
                  onClick={(e) =>
                    go(`/NovaCommissionTeam?orderNo=${record.orderNo}`, e)
                  }
                >
                  <Network size={17} />
                  Nova Network
                </button>
                <button
                  type="button"
                  onClick={(e) =>
                    go(`/Tree?orderNo=${record.orderNo}&type=nova`, e)
                  }
                >
                  <TreePine size={17} />
                  Nova Tree
                </button>
              </div>

              <span className="ri-mobile-tap-note">
                Tap card to view complete incentive details
              </span>
            </article>
          ))}
          {records.length === 0 && (
            <div className="ri-mobile-main-card">
              <span className="ri-mobile-label">No data available</span>
            </div>
          )}
        </div>

        <Pagenation
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </section>
  );
};

export default ReferralCommission;
